import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import toastUtils from '../utils/toast-utils';
import { X, Save, Upload, ChevronDown, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CustomDropdown = ({ options, value, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-2 bg-white dark:bg-gray-800 border border-[#E5E5E5] dark:border-gray-700 rounded-lg shadow-sm focus:ring-2 focus:ring-[#D0021B]/50 focus:border-[#D0021B] focus:outline-none transition-all duration-300 text-[#1A1A1A] dark:text-white text-left ${disabled ? 'opacity-50 cursor-not-allowed bg-[#F5F5F5] dark:bg-gray-900' : 'hover:border-[#D0021B]/50 cursor-pointer'}`}
      >
        <span className="truncate pr-4 font-medium">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-[#666666] dark:text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#D0021B]' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute z-[60] w-full mt-1 bg-white dark:bg-gray-900 border border-[#E5E5E5] dark:border-gray-700 rounded-xl shadow-xl overflow-hidden max-h-60 flex flex-col"
          >
            <style>{`
              .form-scrollbar::-webkit-scrollbar { width: 5px; }
              .form-scrollbar::-webkit-scrollbar-track { background: transparent; }
              .form-scrollbar::-webkit-scrollbar-thumb { background-color: #D0021B; border-radius: 10px; opacity: 0.5; }
              .form-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #A50015; }
            `}</style>
            <div className="p-1 overflow-y-auto form-scrollbar flex-1">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                  value === '' 
                    ? 'bg-[#D0021B]/10 text-[#D0021B] font-semibold' 
                    : 'text-[#666666] dark:text-gray-400 hover:bg-[#F5F5F5] dark:hover:bg-gray-800 hover:text-[#1A1A1A] dark:hover:text-white'
                }`}
              >
                <span>{placeholder}</span>
                {value === '' && <Check className="w-4 h-4" />}
              </button>
              {options.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 mt-1 text-sm rounded-lg transition-all duration-200 ${
                    String(value) === String(opt.value)
                      ? 'bg-[#D0021B]/10 text-[#D0021B] font-semibold' 
                      : 'text-[#666666] dark:text-gray-400 hover:bg-[#F5F5F5] dark:hover:bg-gray-800 hover:text-[#1A1A1A] dark:hover:text-white'
                  }`}
                >
                  <span className="truncate pr-4">{opt.label}</span>
                  {String(value) === String(opt.value) && <Check className="w-4 h-4 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function CardFormModal({ cardId, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const isEditing = !!cardId;

  const [formData, setFormData] = useState({
    id_number: '',
    full_name: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    email: '',
    expiry_date: '',
    designation_id: '',
    card_status: 'active',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [idProofFile, setIdProofFile] = useState(null);
  const [idProofPreview, setIdProofPreview] = useState(null);
  const [idProofType, setIdProofType] = useState('');

  // Fetch card data if editing
  const { data: cardData, isLoading: cardLoading } = useQuery({
    queryKey: ['card', cardId],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/cards/${cardId}`);
      return data;
    },
    enabled: isEditing,
  });

  // Fetch designations
  const { data: designations } = useQuery({
    queryKey: ['designations'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/designations`);
      return data;
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (cardData) {
      setFormData({
        id_number: cardData.id_number || '',
        full_name: cardData.full_name || '',
        mobile: cardData.mobile || '',
        address: cardData.address || '',
        city: cardData.city || '',
        state: cardData.state || '',
        email: cardData.email || '',
        expiry_date: cardData.expiry_date ? new Date(cardData.expiry_date).toISOString().split('T')[0] : '',
        designation_id: cardData.designation_id || '',
        card_status: cardData.card_status || 'active',
      });
      if (cardData.photo_path) {
        setPhotoPreview(`${API_URL}/src/photos/${cardData.photo_path}`);
      } else if (cardData.photo_base64) {
        setPhotoPreview(cardData.photo_base64);
      }
      if (cardData.id_proof_path) {
        setIdProofPreview(`${API_URL}/api/cards/${cardData.card_id}/id-proof`);
      }
      if (cardData.id_proof_type) {
        setIdProofType(cardData.id_proof_type);
      }
    }
  }, [cardData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const formDataObj = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          formDataObj.append(key, value);
        }
      });
      if (photoFile) {
        formDataObj.append('photo', photoFile);
      }
      if (idProofFile) {
        formDataObj.append('id_proof', idProofFile);
        if (idProofType) formDataObj.append('id_proof_type', idProofType);
      }
      const { data: response } = await axios.post(`${API_URL}/api/cards`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    },
    onSuccess: () => {
      toastUtils.success('Card created successfully');
      queryClient.invalidateQueries(['cards']);
      queryClient.invalidateQueries(['dashboard-stats']);
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toastUtils.error(error.response?.data?.detail || 'Failed to create card');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const formDataObj = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formDataObj.append(key, value);
        }
      });
      if (photoFile) {
        formDataObj.append('photo', photoFile);
      }
      if (idProofFile) {
        formDataObj.append('id_proof', idProofFile);
        if (idProofType) formDataObj.append('id_proof_type', idProofType);
      } else if (idProofType) {
        // update type even if no new file uploaded
        formDataObj.append('id_proof_type', idProofType);
      }
      const { data: response } = await axios.put(`${API_URL}/api/cards/${cardId}`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    },
    onSuccess: () => {
      toastUtils.success('Card updated successfully');
      queryClient.invalidateQueries(['cards']);
      queryClient.invalidateQueries(['card', cardId]);
      queryClient.invalidateQueries(['dashboard-stats']);
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toastUtils.error(error.response?.data?.detail || 'Failed to update card');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.expiry_date) {
      toastUtils.error('Full name and expiry date are required');
      return;
    }
    
    if (!isEditing && !formData.id_number) {
      toastUtils.error('ID number is required');
      return;
    }

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      // Prepend "ID No: " to matches user requirement for storage and preview
      const finalData = {
        ...formData,
        id_number: formData.id_number.startsWith('ID No: ') 
          ? formData.id_number 
          : `ID No: ${formData.id_number}`
      };
      createMutation.mutate(finalData);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIdProofChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setIdProofFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setIdProofPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        // PDF or other doc – store name as preview sentinel
        setIdProofPreview('__pdf__:' + file.name);
      }
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditing && cardLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D0021B] border-t-transparent"></div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25 }}
        className="bg-white dark:bg-[#0F172A] rounded-[2.5rem] max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/20 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
        data-testid="card-form-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E5E5] dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <h2 className="font-serif text-xl font-bold text-[#1A1A1A] dark:text-white">
            {isEditing ? 'Edit Press Card' : 'Add New Press Card'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-[#666666] dark:text-gray-400 hover:text-[#D0021B] hover:bg-white dark:hover:bg-gray-700 rounded-xl transition-all shadow-sm"
            data-testid="close-form-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            {/* Photo Upload */}
            <div className="flex items-center gap-5 p-4 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-gray-100 dark:border-gray-800">
              <div className="w-24 h-32 bg-gray-200 dark:bg-gray-800 rounded-xl overflow-hidden shadow-inner flex-shrink-0 border-2 border-white dark:border-gray-700">
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600">
                    <Upload className="w-8 h-8 opacity-20" />
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:border-[#D0021B] hover:text-[#D0021B] transition-all shadow-sm">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Choose Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    data-testid="photo-input"
                  />
                </label>
                <p className="text-[11px] text-[#666666] dark:text-gray-500 mt-2 ml-1">JPEG or PNG (max 5MB)</p>
              </div>
            </div>

            {/* ID Number */}
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">
                ID Number <span className="text-[#D0021B]">*</span>
              </label>
              <input
                type="text"
                value={formData.id_number}
                onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all font-mono text-gray-900 dark:text-white disabled:opacity-60"
                placeholder="FIN/2015/DL0001"
                disabled={isEditing}
                data-testid="id-number-input"
              />
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">
                Full Name <span className="text-[#D0021B]">*</span>
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all text-gray-900 dark:text-white font-medium"
                placeholder="Enter full name"
                data-testid="full-name-input"
              />
            </div>

            {/* Designation */}
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">Designation</label>
              <CustomDropdown
                options={designations?.map(d => ({ value: d.designation_id, label: d.designation_name })) || []}
                value={formData.designation_id}
                onChange={(val) => setFormData({ ...formData, designation_id: val })}
                placeholder="Select Designation"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">Mobile</label>
              <input
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all text-gray-900 dark:text-white font-medium"
                placeholder="+91-9876543210"
                data-testid="mobile-input"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all text-gray-900 dark:text-white font-medium"
                placeholder="email@example.com"
                data-testid="email-input"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all resize-none text-gray-900 dark:text-white font-medium"
                rows={3}
                placeholder="Enter address"
                data-testid="address-input"
              />
            </div>

            {/* City and State Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all text-gray-900 dark:text-white font-medium"
                  placeholder="Enter City"
                  data-testid="city-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all text-gray-900 dark:text-white font-medium"
                  placeholder="Enter State"
                  data-testid="state-input"
                />
              </div>
            </div>

            {/* ID Proof Upload */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-3">
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">
                ID Proof
            
              </label>

              {/* Proof Type Selector */}
              <div className="flex flex-wrap gap-2">
                {['Aadhaar Card', 'PAN Card', 'Driving Licence', 'Passport', 'Voter ID', 'Other'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setIdProofType(type)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      idProofType === type
                        ? 'bg-[#D0021B] text-white border-[#D0021B] shadow-md shadow-[#D0021B]/20'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-[#D0021B]/50 hover:text-[#D0021B]'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>

              {/* File Picker + Preview */}
              <div className="flex items-center gap-4">
                {/* Preview box */}
                <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-800 border-2 border-white dark:border-gray-700 shadow-inner flex items-center justify-center">
                  {idProofPreview ? (
                    idProofPreview.startsWith('__pdf__:') ? (
                      <div className="flex flex-col items-center gap-1 p-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#D0021B]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                        </svg>
                        <span className="text-[9px] text-gray-500 dark:text-gray-400 text-center leading-tight truncate w-16">
                          {idProofPreview.replace('__pdf__:', '')}
                        </span>
                      </div>
                    ) : (
                      <img src={idProofPreview} alt="ID Proof" className="w-full h-full object-cover" />
                    )
                  ) : (
                    <Upload className="w-7 h-7 text-gray-300 dark:text-gray-600" />
                  )}
                </div>

                {/* Upload button */}
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:border-[#D0021B] hover:text-[#D0021B] transition-all shadow-sm">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {idProofFile ? 'Change File' : 'Upload Proof'}
                    </span>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleIdProofChange}
                      className="hidden"
                      data-testid="id-proof-input"
                    />
                  </label>
                  <p className="text-[11px] text-[#666666] dark:text-gray-500 ml-1">
                    {idProofFile
                      ? <span className="text-green-600 dark:text-green-400 font-medium">✓ {idProofFile.name}</span>
                      : 'JPG, PNG or PDF (max 10 MB)'}
                  </p>
                </div>
              </div>
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">
                Expiry Date <span className="text-[#D0021B]">*</span>
              </label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                className="w-full px-4 py-3 bg-[#F8FAFC] dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-xl focus:ring-4 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/30 transition-all text-gray-900 dark:text-white font-medium"
                data-testid="expiry-date-input"
              />
            </div>

            {/* Status (only for editing) */}
            {isEditing && (
              <div className="relative">
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5 ml-1">Status</label>
                <CustomDropdown
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'expired', label: 'Expired' },
                    { value: 'suspended', label: 'Suspended' },
                    { value: 'pending', label: 'Pending' }
                  ]}
                  value={formData.card_status}
                  onChange={(val) => setFormData({ ...formData, card_status: val })}
                  placeholder="Select Status"
                />
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E5E5E5] dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[#666666] dark:text-gray-400 font-bold hover:text-[#1A1A1A] dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isPending}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#D0021B] text-white rounded-xl font-bold hover:bg-[#A50015] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#D0021B]/20 active:scale-95"
            data-testid="save-card-button"
          >
            {isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEditing ? 'Update Card' : 'Create Card'}
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
